/**
 * Controles de calidad de §9.4.
 *
 * Checklist literal del documento:
 *  - GPS obligatorio: validar que cada captura cae dentro del polígono de la
 *    sección INE.
 *  - Duración mínima: entrevistas con duración < 10 min se auditan y, en su
 *    caso, se anulan.
 *  - Supervisión telefónica del 20% a las 24–48 h (confirmar la entrevista y
 *    5 respuestas clave).
 *  - Supervisión presencial del 10%.
 *  - Revisión diaria de: distribución por género × edad × estrato, tasa de
 *    rechazo por encuestador, patrones repetidos (straight-lining) y
 *    respuestas "NS" anómalas.
 *  - Reprogramación de horarios (tarde y fin de semana) a partir del día 6 si
 *    hay subcobertura de jóvenes u hombres.
 */
/* -------------------------------------------------------------------------- */
/*                                 Umbrales                                   */
/* -------------------------------------------------------------------------- */
export const QUALITY_THRESHOLDS = {
    /** §9.4 — duración mínima en minutos; por debajo se audita. */
    minDurationMinutes: 10,
    /** §2 — duración de diseño de la entrevista. */
    expectedDurationMinutes: { min: 18, max: 20 },
    /** §9.4 — proporción de entrevistas con verificación telefónica. */
    phoneVerificationShare: 0.2,
    /** §9.4 — proporción con verificación presencial. */
    inPersonVerificationShare: 0.1,
    /** §9.4 — ventana de la verificación telefónica. */
    phoneVerificationWindowHours: { min: 24, max: 48 },
    /** §9.4 — número de respuestas clave a confirmar en la llamada. */
    verificationKeyAnswers: 5,
    /**
     * Proporción de respuestas idénticas consecutivas dentro de una batería a
     * partir de la cual se considera straight-lining. No viene en el documento,
     * que sólo pide detectar "patrones repetidos"; es el umbral operativo y es
     * configurable desde el panel.
     */
    straightLiningRatio: 0.9,
    /** Baterías con menos filas que esto no se evalúan por straight-lining. */
    straightLiningMinRows: 5,
    /**
     * Proporción de respuestas no sustantivas (NS/NC) a partir de la cual la
     * entrevista se marca por "respuestas NS anómalas". Umbral operativo
     * configurable; el documento no fija un valor.
     */
    anomalousNonSubstantiveRatio: 0.3,
    /** §2 — merma esperada por control de calidad (880 programadas → 800). */
    expectedAnnulmentRate: 0.1,
    /** §9.4 — día de campo a partir del cual se evalúa reprogramar horarios. */
    rescheduleReviewDay: 6,
    /**
     * Precisión GPS máxima aceptable en metros para dar la autodetección por
     * concluyente. Por encima se pide al encuestador reintentar la lectura.
     */
    maxGpsAccuracyMeters: 50,
    /**
     * Tolerancia en metros alrededor del polígono: una captura hasta esta
     * distancia del borde se trata como dentro, porque el error del GPS de un
     * teléfono y la precisión del trazo cartográfico son del mismo orden.
     */
    polygonBufferMeters: 30,
};
/* -------------------------------------------------------------------------- */
/*                               Banderas                                     */
/* -------------------------------------------------------------------------- */
export const QUALITY_FLAGS = [
    'duration-below-minimum',
    'duration-above-expected',
    'gps-outside-polygon',
    'gps-missing',
    'gps-low-accuracy',
    'straight-lining',
    'anomalous-non-substantive',
    'third-party-present',
    'section-mismatch',
    'eligibility-contradiction',
    'i6-unknown-personaje',
    'duplicate-household',
    'high-refusal-rate-interviewer',
];
export const QUALITY_FLAG_LABELS = {
    'duration-below-minimum': 'Duración menor a 10 minutos',
    'duration-above-expected': 'Duración muy por encima de lo esperado',
    'gps-outside-polygon': 'GPS fuera del polígono de la sección',
    'gps-missing': 'Sin coordenada GPS',
    'gps-low-accuracy': 'Precisión GPS insuficiente',
    'straight-lining': 'Patrón repetido en baterías (straight-lining)',
    'anomalous-non-substantive': 'Exceso de respuestas NS/NC',
    'third-party-present': 'Presencia de terceros durante la entrevista',
    'section-mismatch': 'La sección detectada no coincide con la asignada',
    'eligibility-contradiction': 'Contradicción en los filtros de elegibilidad',
    'i6-unknown-personaje': 'I6 se aplicó sobre un personaje que I1 marcó como desconocido',
    'duplicate-household': 'Posible vivienda duplicada',
    'high-refusal-rate-interviewer': 'Tasa de rechazo atípica del encuestador',
};
/** Banderas que por sí solas obligan a auditoría antes de aceptar la entrevista. */
export const BLOCKING_FLAGS = [
    'duration-below-minimum',
    'gps-missing',
    'eligibility-contradiction',
];
export const FLAG_SEVERITY = {
    'duration-below-minimum': 'critical',
    'gps-missing': 'critical',
    'eligibility-contradiction': 'critical',
    // Advertencia, no crítica: la respuesta existe y es un dato real. Lo que
    // falla es la base sobre la que se calcula el indicador de §10.1.4, que
    // asume que quien contesta I6 conoce al personaje.
    'i6-unknown-personaje': 'warning',
    'gps-outside-polygon': 'warning',
    'section-mismatch': 'warning',
    'straight-lining': 'warning',
    'anomalous-non-substantive': 'warning',
    'duplicate-household': 'warning',
    'high-refusal-rate-interviewer': 'warning',
    'gps-low-accuracy': 'info',
    'duration-above-expected': 'info',
    'third-party-present': 'info',
};
/* -------------------------------------------------------------------------- */
/*                          Detección de patrones                            */
/* -------------------------------------------------------------------------- */
/**
 * Straight-lining: proporción de la batería contestada con el MISMO código.
 *
 * Se mide por batería y no sobre la entrevista completa, porque las escalas
 * son distintas entre bloques y una misma respuesta en dos escalas distintas
 * no es un patrón. Devuelve null si la batería es demasiado corta para que la
 * medición signifique algo.
 */
export function straightLiningScore(answers) {
    const given = answers.filter((a) => a !== null && a !== '');
    if (given.length < QUALITY_THRESHOLDS.straightLiningMinRows)
        return null;
    const counts = new Map();
    for (const a of given)
        counts.set(a, (counts.get(a) ?? 0) + 1);
    const max = Math.max(...counts.values());
    return max / given.length;
}
export function isStraightLining(answers, ratio = QUALITY_THRESHOLDS.straightLiningRatio) {
    const score = straightLiningScore(answers);
    return score !== null && score >= ratio;
}
/**
 * Proporción de respuestas no sustantivas (NS/NC/"no tiene opinión") sobre el
 * total de preguntas efectivamente aplicadas.
 *
 * El denominador son las preguntas APLICADAS, no todas las del instrumento:
 * quien conoce a 3 de los 11 personajes no contesta I2–I5 de los otros 8, y eso
 * no es no respuesta.
 */
export function nonSubstantiveRatio(appliedCount, nonSubstantiveCount) {
    if (appliedCount === 0)
        return 0;
    return nonSubstantiveCount / appliedCount;
}
export function isAnomalousNonSubstantive(appliedCount, nonSubstantiveCount, ratio = QUALITY_THRESHOLDS.anomalousNonSubstantiveRatio) {
    return nonSubstantiveRatio(appliedCount, nonSubstantiveCount) >= ratio;
}
/** Duración en minutos entre inicio y fin (Q3). */
export function durationMinutes(startedAt, finishedAt) {
    const a = typeof startedAt === 'string' ? new Date(startedAt) : startedAt;
    const b = typeof finishedAt === 'string' ? new Date(finishedAt) : finishedAt;
    return (b.getTime() - a.getTime()) / 60_000;
}
export function selectForVerification(candidates, mode, rng, 
// Anotado como number a propósito: sin la anotación, TypeScript infiere el
// tipo literal de los umbrales (0.2 | 0.1) y rechazaría cualquier otra
// proporción, que es justo lo que el panel permite ajustar.
share = mode === 'phone'
    ? QUALITY_THRESHOLDS.phoneVerificationShare
    : QUALITY_THRESHOLDS.inPersonVerificationShare) {
    const eligible = mode === 'phone' ? candidates.filter((c) => c.hasPhone) : [...candidates];
    const byInterviewer = new Map();
    for (const c of eligible) {
        if (!byInterviewer.has(c.encuestadorId))
            byInterviewer.set(c.encuestadorId, []);
        byInterviewer.get(c.encuestadorId).push(c);
    }
    const selected = [];
    for (const group of byInterviewer.values()) {
        // Primero las que traen banderas; dentro de cada nivel, al azar.
        const ordered = group
            .map((c) => ({ c, r: rng.next() }))
            .sort((x, y) => y.c.flagCount - x.c.flagCount || x.r - y.r)
            .map((x) => x.c);
        // Al menos una por encuestador cuando el grupo no está vacío.
        const quota = Math.max(1, Math.round(group.length * share));
        selected.push(...ordered.slice(0, quota).map((c) => c.interviewId));
    }
    return selected;
}
//# sourceMappingURL=quality.js.map